import React, { forwardRef, InputHTMLAttributes } from 'react';
import classNames from 'classnames';

import { InputSize } from './types';

import { Icon } from 'app/design-system/components/Icon';
import { Hint } from 'app/design-system/components/Popper';
import { ICON_NAME_HELP } from 'app/design-system/constants/IconNames';
import { IconName } from 'app/design-system/types/Icon';

import { ICON_SIZE_BY_NAME } from './constants';
import IconButton, { IconButtonProps } from './IconButton';

import styles from './Input.module.scss';

// ---

export { InputSize };

type IconProps = Partial<IconButtonProps>;

export type InputType = 'text' | 'tel' | 'email' | 'password' | 'color' | 'number' | 'search' | 'url' | 'time';

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  value: string | number;
  type?: InputType;
  autoComplete?: 'off' | 'on' | 'new-password';
  className?: string;
  // style attributes
  inputSize?: InputSize;
  error?: boolean;
  hintContent?: string;
  units?: JSX.Element | string;
  // icons
  iconLeft?: IconName;
  iconLeftProps?: IconProps;
  iconRight?: IconName;
  iconRightProps?: IconProps;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(({
  value,
  type = 'text',
  onChange,
  disabled = false,
  autoComplete = 'off',
  className,
  inputSize = InputSize.MEDIUM,
  error = false,
  hintContent,
  units,
  iconLeft,
  iconLeftProps,
  iconRight,
  iconRightProps,
  ...props
}, ref) => {
  const rootClassName = classNames(
    styles.root,
    styles[inputSize],
    styles.input,
    {
      [styles.hasIconLeft]: iconLeft,
      [styles.hasIconRight]: iconRight && !units,
      [styles.hasHint]: iconRight && hintContent && !units,
      [styles.hasUnits]: units,
      [styles.error]: error,
      [styles.disabled]: disabled,
    },
    className,
  );

  return (
    <div className={rootClassName}
         data-testid="input-root">
      {iconLeft && (
        <IconButton
            className={styles.iconLeft}
            icon={iconLeft}
            size={ICON_SIZE_BY_NAME[inputSize]}
            disabled={disabled}
            {...iconLeftProps} />
      )}
      <input
          data-testid="input"
          {...props}
          ref={ref}
          disabled={disabled}
          onChange={onChange}
          type={type}
          value={value}
          autoComplete={autoComplete} />
      {iconRight && !units && (
        <>
          {hintContent && (
            <Hint content={hintContent}
                  childrenWrapperClassName={styles.hint}>
              <Icon icon={ICON_NAME_HELP}
                    size={16} />
            </Hint>
          )}
          <IconButton
            icon={iconRight}
            size={ICON_SIZE_BY_NAME[inputSize]}
            disabled={disabled}
            {...iconRightProps}
            className={classNames(styles.iconRight, iconRightProps?.className)} />
        </>
      )}
      {units && (
        <span className={styles.units}>{units}</span>
      )}
    </div>
  );
});
